import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/main/authSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import PageHeader from "../../components/PageHeader";
import Spinner from "../../components/Spinner";
import Alert from "../../components/Alert";
import { formatDate } from "../../utils/formatters";

export default function CustomerSystems() {
  const authenticatedUser = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  const accountId = accountData?._id;
  
  // Recuperiamo l'ID cliente da localStorage
  const userData = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
  const customerId = userData?.customerId || authenticatedUser?.customerId;
  
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carica gli impianti del cliente
  const loadSystems = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!customerId) {
        throw new Error("ID cliente non disponibile");
      }

      console.log("Recupero impianti con:", {
        customerId,
        accountId
      });

      // Chiamata API per recuperare gli impianti del cliente
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/systems/systemsList?accountId=${accountId}&customerId=${customerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-token": localStorage.getItem("installamelo-auth-token")
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Errore durante il recupero degli impianti");
      }

      const data = await response.json();
      console.log("Impianti recuperati:", data);
      setSystems(data);
    } catch (err) {
      console.error("Errore durante il caricamento degli impianti:", err);
      setError(err.message || "Si è verificato un errore durante il caricamento degli impianti");
    } finally {
      setLoading(false);
    }
  };

  // Carica gli impianti all'avvio della pagina
  useEffect(() => {
    if (accountId && customerId) {
      loadSystems();
    }
  }, [accountId, customerId]);

  // Verifica se un impianto è ancora in garanzia
  const isUnderWarranty = (warrantyExpirationDate) => {
    if (!warrantyExpirationDate) return false;
    
    const today = new Date();
    const expirationDate = new Date(warrantyExpirationDate);
    return today <= expirationDate;
  };

  // Formatta una data in formato italiano
  const formatDateIt = (date) => {
    if (!date) return "N/D";
    
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(date).toLocaleDateString('it-IT', options);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader title="I Miei Impianti" />

      {error && (
        <Alert 
          variant="danger" 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}

      {loading ? (
        <div className="flex justify-center my-8">
          <Spinner size="lg" />
        </div>
      ) : systems.length === 0 ? (
        <div className="bg-white shadow-md rounded-lg p-6 text-center">
          <p className="text-gray-600">Non sono presenti impianti associati al tuo account.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {systems.map((system) => (
            <div key={system._id} className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="p-5 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">{system.title}</h3>
                <p className="text-gray-600 mt-1">
                  {system.customerAddressLabel}
                </p>
              </div>
              
              <div className="p-5">
                <h4 className="font-medium text-gray-700 mb-2">Tipi di impianti</h4>
                {system.typesList && system.typesList.length > 0 ? (
                  <div className="space-y-4">
                    {system.typesList.map((type, idx) => (
                      <div key={type._id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        <p className="font-medium text-primary">
                          {type.typeLabel || "Tipo impianto non specificato"}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <div>
                            <span className="text-gray-500">Data collaudo:</span>
                            <p>{formatDateIt(type.testedDate)}</p>
                          </div>
                          
                          <div>
                            <span className="text-gray-500">Scadenza garanzia:</span>
                            <p>{formatDateIt(type.expirationWarrantyDate)}</p>
                          </div>
                        </div>
                        
                        {type.expirationWarrantyDate && (
                          <div className="mt-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              isUnderWarranty(type.expirationWarrantyDate)
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}>
                              {isUnderWarranty(type.expirationWarrantyDate)
                                ? "In garanzia"
                                : "Garanzia scaduta"}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Nessun tipo di impianto specificato</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
